import { Col, Form, Row } from 'antd';
import React from 'react';
import { CurrencyInput, DatePicker, Modal } from '@common/components';
import { useMutation } from '@common/hooks';
import { dollarsToCents } from '@common/services';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  CreateInvoiceInstallmentArgs,
  CreateInvoiceInstallmentMutation,
  CreateInvoiceInstallmentResponse,
} from '@pay-bill/graphql';
import { US_DATE_FORMAT } from 'works/legacy/lib/date-helper';
import { Store } from 'antd/lib/form/interface';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { datetime } from '@common/services/datetime';

export const CreateInstallmentModal = ({
  visible,
  onAction,
  worksInvoiceId,
  remainingInstallmentBalance,
}: {
  visible: boolean;
  onAction: () => void;
  worksInvoiceId: string;
  remainingInstallmentBalance: number;
}) => {
  const [form] = Form.useForm();

  const [performCreate, { loading }] = useMutation<
    CreateInvoiceInstallmentResponse,
    CreateInvoiceInstallmentArgs
  >(CreateInvoiceInstallmentMutation, {
    onCompleted: (data) => {
      if (data?.createInvoiceInstallment?.errors) {
        createMessage('error', 'Request failed. Please try again.');
        addErrorsToForm(form, data.createInvoiceInstallment.errors);
      } else {
        createMessage('success', `Payment successfully saved`);
        onAction();
      }
    },
    onError: () => {
      createMessage('error', 'Request failed. Please try again.');
    },
    refetchQueries: ['GetWorksInvoice'],
  });

  const onCreate = (values: Store) => {
    performCreate({
      variables: {
        worksInvoiceId: worksInvoiceId,
        input: {
          paymentAmountCents: dollarsToCents(values.paymentAmount),
          paidAt: values.paidAt,
        },
      },
    });
  };

  const initialValues = {
    paidAt: datetime(),
    paymentAmount: 0,
  };

  return (
    <Modal
      visible={visible}
      cancelText="Cancel"
      okText="Add"
      onCancel={onAction}
      title="Add Payment"
      confirmLoading={loading}
      onOk={form.submit}
      className="create-installment-modal"
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={onCreate}
        initialValues={initialValues}
      >
        <Row gutter={[12, 0]}>
          <Col span={12}>
            <Form.Item
              name="paymentAmount"
              label="Amount Paid"
              rules={[
                { required: true, message: 'Please select the amount paid' },
                {
                  max: remainingInstallmentBalance / 100,
                  type: 'number',
                  message: 'Amount Paid is more than the remaining balance.',
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <CurrencyInput size="large" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="paidAt"
              label="Date Paid"
              rules={[
                { required: true, message: 'Please select the date paid' },
              ]}
              style={{ marginBottom: 0 }}
            >
              <DatePicker
                format={US_DATE_FORMAT}
                size="large"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
